import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { ErrorState, ForbiddenState, SkeletonRows } from '@/components/admin/states';
import { fetchStaff } from '@/features/staff/api';
import { STAFF_ROLE_META, type StaffRole } from '@/features/staff/StaffPage';
import { StaffProfileEditor } from '@/features/staff/StaffProfileEditor';
import { ApiError } from '@/lib/api/http';

/**
 * 理事・講師／サクセス担当のプロフィール代理編集（事務局のみ / D-142・D-143）。
 * ふだんはほとんど触らない編集操作を一覧画面から分けた（会員詳細と同じ考え方）。
 */
export function StaffProfileEditPage({ role }: { role: StaffRole }): ReactElement {
  const { userId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meta = STAFF_ROLE_META[role];

  const query = useQuery({ queryKey: ['staff', role], queryFn: () => fetchStaff({ role }) });

  if (query.isPending) return <SkeletonRows rows={6} />;
  if (query.isError) {
    const forbidden = query.error instanceof ApiError && query.error.status === 403;
    return forbidden ? (
      <ForbiddenState />
    ) : (
      <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />
    );
  }

  const staff = query.data.find((s) => s.user_id === userId);
  if (!staff) return <ErrorState message="担当者が見つかりません。" />;

  return (
    <>
      <PageHeader title={`${staff.display_name ?? staff.email}さんのプロフィール編集`} />
      <StaffProfileEditor
        staff={staff}
        onSaved={async () => {
          await queryClient.invalidateQueries({ queryKey: ['staff'] });
          navigate(meta.path);
        }}
        onClose={() => navigate(meta.path)}
      />
      <button
        type="button"
        onClick={() => navigate(meta.path)}
        className="mt-2 text-wpaccent hover:underline"
      >
        ← 一覧に戻る
      </button>
    </>
  );
}
